import { useState, useEffect } from 'react';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { useAuth } from '../../contexts/AuthContext';
import LockedContentOverlay from '../upgrade/LockedContentOverlay';

const SCORE_COLORS = ['','bg-[#10b981]','bg-[#10b981]','bg-[#34d399]','bg-[#eab308]','bg-[#eab308]','bg-[#f59e0b]','bg-[#f97316]','bg-[#f97316]','bg-[#ef4444]','bg-[#dc2626]'];
const ASSESSMENT = {
  underreaction: { text: 'Underreaction', cls: 'text-[#f59e0b]' },
  overreaction: { text: 'Overreaction', cls: 'text-[#3b82f6]' },
  properly_priced: { text: 'Properly Priced', cls: 'text-[#10b981]' },
  not_yet_priced: { text: 'Not Priced In', cls: 'text-[#ef4444]' },
};
const STATUS_CLS = {
  Out: 'bg-[rgba(239,68,68,0.1)] text-[#ef4444]',
  Questionable: 'bg-[rgba(234,179,8,0.1)] text-[#eab308]',
  Probable: 'bg-[rgba(16,185,129,0.1)] text-[#10b981]',
  Doubtful: 'bg-[rgba(249,115,22,0.1)] text-[#f97316]',
};

export default function InjuryImpact({ eventId, sport, hasInjuries, homeTeam, awayTeam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const tier = useEffectiveTier();
  const { loading: authLoading } = useAuth();

  useEffect(() => {
    if (!hasInjuries || authLoading) return;
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({ sport });
    if (homeTeam) params.set('home_team', homeTeam);
    if (awayTeam) params.set('away_team', awayTeam);
    betClient.get(`/api/ai/injury-impact/${eventId}?${params.toString()}`, { signal: controller.signal })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [eventId, sport, hasInjuries, homeTeam, awayTeam, tier, authLoading]);

  if (!hasInjuries) return null;
  if (loading) return <div className="rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-5 py-4"><SH>Injury Impact</SH><div className="h-24 skeleton rounded mt-3" /></div>;
  if (!data?.injuries?.length) return null;

  return (
    <div className="rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-5 py-4">
      <SH>Injury Impact</SH>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        {data.injuries.map((inj, i) => <InjuryCard key={i} injury={inj} />)}
      </div>
    </div>
  );
}

function SH({ children }) {
  return <div className="flex items-center gap-2"><div className="w-[3px] h-4 bg-[#3b82f6] rounded-full" /><h3 className="text-[12px] uppercase tracking-[0.05em] font-semibold text-[#64748b] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{children}</h3></div>;
}

function InjuryCard({ injury }) {
  const score = injury.impact_score;
  const bar = score >= 1 && score <= 10 ? SCORE_COLORS[score] : 'bg-[#505a6e]';
  const assess = ASSESSMENT[injury.market_assessment];

  return (
    <div className="rounded bg-[#f8fafc] dark:bg-[#0f1219] p-3">
      <div className="flex items-center justify-between flex-wrap gap-1 mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] font-semibold text-[#1e293b] dark:text-[#e2e8f0]">{injury.player}</span>
          <span className="text-[10px] text-[#8892a8]">{injury.position} &middot; {injury.team}</span>
        </div>
        <span className={`text-[9px] font-semibold px-1 py-px rounded ${STATUS_CLS[injury.status] || 'bg-[#1a2030] text-[#8892a8]'}`}>{injury.status}</span>
      </div>

      {score != null && (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] text-[#8892a8]">Impact</span>
            <span className="text-[12px] font-bold text-[#1e293b] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{score}/10</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#e2e8f0] dark:bg-[#1a2030] overflow-hidden">
            <div className={`h-full rounded-full ${bar}`} style={{ width: `${score * 10}%` }} />
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-2 text-center">
        {injury.expected_movement != null && (
          <div className="flex-1 rounded bg-white dark:bg-[#151a25] p-2">
            <p className="text-[9px] text-[#8892a8] uppercase">Expected</p>
            <p className="text-[12px] font-bold text-[#1e293b] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{injury.expected_movement} pts</p>
          </div>
        )}
        {injury.actual_movement != null && (
          <div className="flex-1 rounded bg-white dark:bg-[#151a25] p-2">
            <p className="text-[9px] text-[#8892a8] uppercase">Actual</p>
            <p className="text-[12px] font-bold text-[#1e293b] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{injury.actual_movement} pts</p>
          </div>
        )}
        {assess && (
          <div className="flex-1 rounded bg-white dark:bg-[#151a25] p-2">
            <p className="text-[9px] text-[#8892a8] uppercase">Market</p>
            <p className={`text-[12px] font-bold ${assess.cls}`}>{assess.text}</p>
          </div>
        )}
      </div>

      {injury.explanation ? (
        <p className="text-[11px] text-[#475569] dark:text-[#8892a8] leading-relaxed">{injury.explanation}</p>
      ) : (
        <>
          {injury.teaser_hook && <p className="text-[11px] text-[#475569] dark:text-[#8892a8] leading-relaxed italic mb-2">{injury.teaser_hook}</p>}
          <LockedContentOverlay
            description="See how this injury affects the line and which side it favors."
            triggerFeature="injury_impact"
          />
        </>
      )}
    </div>
  );
}
